import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import styled from 'styled-components/macro'
import { StockSurfaceCard } from 'src/components/stock/StockPageChrome'
import {
  WASTAGE_COPY,
  WASTAGE_STOCK_HOME_CARD,
  wastageBackTarget,
  wastageLinkTo,
  pickWastageWaitingDay,
  pickWastageWaitingNextStep,
  wastageHomeCardCostLabel,
  wastageLondonMonthRange,
  wastageLondonToday,
} from 'src/constants/wastage'
import { FOOD_SAFETY_SURFACE } from 'src/constants/foodSafety'
import { listWastageEntries } from 'src/utils/wastageApi'

/**
 * Today's waste cost for the Stock card and the Waste page.
 * Undefined while loading. Null when the cost cannot be shown.
 */
export function useWastageTodayCost(
  venueId,
  {
    listEntries = listWastageEntries,
    today = wastageLondonToday(),
    reloadKey = 0,
  } = {},
) {
  const [pence, setPence] = useState(undefined)

  useEffect(() => {
    if (!venueId) return undefined
    let cancelled = false
    listEntries(venueId, { from: today, to: today })
      .then(data => {
        if (!cancelled) setPence(data?.totals?.costValuePence ?? null)
        return null
      })
      .catch(() => {
        if (!cancelled) setPence(null)
        return null
      })
    return () => {
      cancelled = true
    }
  }, [venueId, today, listEntries, reloadKey])

  return pence
}

const WasteHomeCard = ({
  venueId,
  stockTakes = [],
  listEntries = listWastageEntries,
  today = wastageLondonToday(),
  onWaitingNextStep,
}) => {
  const pence = useWastageTodayCost(venueId, { listEntries, today })
  const [waitingEntries, setWaitingEntries] = useState([])
  useEffect(() => {
    if (!venueId) return undefined
    let cancelled = false
    const month = wastageLondonMonthRange(today)
    listEntries(venueId, month)
      .then(data => {
        if (!cancelled) {
          setWaitingEntries(Array.isArray(data?.entries) ? data.entries : [])
        }
        return null
      })
      .catch(() => {
        if (!cancelled) setWaitingEntries([])
        return null
      })
    return () => {
      cancelled = true
    }
  }, [venueId, today, listEntries])
  const waitingDay = pickWastageWaitingDay(waitingEntries)
  const costLabel =
    pence === undefined
      ? null
      : wastageHomeCardCostLabel({
          costValuePence: pence,
          waitingDay,
        })
  const nextStep = waitingDay
    ? pickWastageWaitingNextStep({ stockTakes, waitingDay })
    : null
  const wasteHref = wastageLinkTo(
    `/v/${venueId}/stock/wastage`,
    wastageBackTarget(venueId),
  )

  if (!venueId) return null

  return (
    <CardWrap data-testid='stock-waste-card'>
      <Card data-accent='lime'>
        <CardLink to={wasteHref} data-testid='stock-waste-link'>
          <CardHead>
            <CardTitle>{WASTAGE_STOCK_HOME_CARD.title}</CardTitle>
            <OpenAction data-testid='stock-waste-open'>
              {WASTAGE_COPY.record}
              <OpenMark aria-hidden='true'>›</OpenMark>
            </OpenAction>
          </CardHead>
          <CardLine>{WASTAGE_STOCK_HOME_CARD.line}</CardLine>
          {costLabel ? (
            <CardCost data-testid='stock-waste-today-cost'>
              {costLabel}
            </CardCost>
          ) : null}
        </CardLink>
        {nextStep ? (
          <WaitingCta
            type='button'
            data-testid='stock-waste-waiting-cta'
            data-kind={nextStep.kind}
            onClick={event => {
              event.preventDefault()
              event.stopPropagation()
              onWaitingNextStep?.(nextStep)
            }}
          >
            {nextStep.label}
          </WaitingCta>
        ) : null}
      </Card>
    </CardWrap>
  )
}

const CardWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin: 0 0 ${({ theme }) => theme.spacing.md};
`

const WaitingCta = styled.button`
  align-self: flex-start;
  padding: 0.35rem 0.75rem;
  border: 1.5px solid ${({ theme }) => theme.colours.panSilver || '#e0dfdf'};
  border-radius: ${FOOD_SAFETY_SURFACE.CONTROL_RADIUS};
  background: ${({ theme }) => theme.colours.mayoWhite || '#fff'};
  color: ${({ theme }) => theme.colours.seaweedGreen || '#132d07'};
  font-weight: 600;
  font-size: 0.85rem;
  line-height: 1.2;
  cursor: pointer;

  &:hover,
  &:focus {
    border-color: ${({ theme }) => theme.colours.lettuceGreen || '#b5d922'};
    outline: none;
  }
`

const Card = styled(StockSurfaceCard)`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin: 0;
`

const CardLink = styled(Link)`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  text-decoration: none;
  color: inherit;
  cursor: pointer;

  &:hover,
  &:focus {
    outline: none;
  }

  &:hover [data-testid='stock-waste-open'],
  &:focus [data-testid='stock-waste-open'] {
    background: ${FOOD_SAFETY_SURFACE.LIME_WASH};
    border-color: ${({ theme }) => theme.colours.lettuceGreen || '#b5d922'};
  }
`

const CardHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
`

const CardTitle = styled.div`
  font-size: 1.05rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colours.seaweedGreen || '#132d07'};
`

const OpenAction = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  flex: 0 0 auto;
  padding: 0.4rem 0.75rem;
  border: 1.5px solid ${({ theme }) => theme.colours.panSilver || '#e0dfdf'};
  border-radius: ${FOOD_SAFETY_SURFACE.CONTROL_RADIUS};
  background: ${({ theme }) => theme.colours.mayoWhite || '#fff'};
  color: ${({ theme }) => theme.colours.seaweedGreen || '#132d07'};
  font-weight: 600;
  font-size: 0.9rem;
  line-height: 1.2;
  white-space: nowrap;
`

const OpenMark = styled.span`
  font-size: 1.15rem;
  line-height: 1;
`

const CardLine = styled.div`
  color: ${({ theme }) => theme.colours.seaweedGreen || '#132d07'};
  font-size: 0.95rem;
  line-height: 1.45;
  max-width: 40rem;
`

const CardCost = styled.div`
  font-weight: 600;
  color: ${({ theme }) => theme.colours.seaweedGreen || '#132d07'};
`

export default WasteHomeCard
